// The card is a pair of tessellated grids (front + back) separated by the card's
// thickness. It is tessellated rather than a single quad so the vertex stage can
// bend it -- a flat card reads as a sprite, a card that flexes reads as cardboard.
//
// The rounded silhouette is not in the geometry. It comes from a signed-distance
// test in the fragment shader resolved through alpha-to-coverage, which keeps the
// corners smooth at any zoom without a separate rim mesh or blend ordering.

/** Physical card: 63 x 88 mm, ~0.32 mm thick. World units are decimetres. */
export const CARD = {
  width: 0.63,
  height: 0.88,
  thickness: 0.0035,
  cornerRadius: 0.030,
} as const;

export const CARD_ASPECT = CARD.width / CARD.height;

export interface CardMesh {
  vertices: GPUBuffer;
  indices: GPUBuffer;
  indexCount: number;
  indexFormat: GPUIndexFormat;
  layout: GPUVertexBufferLayout;
}

const FLOATS_PER_VERTEX = 9; // position(3) normal(3) uv(2) face(1)
const STRIDE = FLOATS_PER_VERTEX * 4;

export const CARD_VERTEX_LAYOUT: GPUVertexBufferLayout = {
  arrayStride: STRIDE,
  attributes: [
    { shaderLocation: 0, offset: 0, format: 'float32x3' },  // position
    { shaderLocation: 1, offset: 12, format: 'float32x3' }, // normal
    { shaderLocation: 2, offset: 24, format: 'float32x2' }, // uv
    { shaderLocation: 3, offset: 32, format: 'float32' },   // face: +1 front, -1 back
  ],
};

export interface CardMeshOptions {
  /** Grid cells across the card width. Higher = smoother bend. */
  segmentsX?: number;
  segmentsY?: number;
}

export function createCardMesh(device: GPUDevice, opts: CardMeshOptions = {}): CardMesh {
  const nx = opts.segmentsX ?? 24;
  const ny = opts.segmentsY ?? 32;

  const cols = nx + 1, rows = ny + 1;
  const perFace = cols * rows;
  const vertexCount = perFace * 2;
  const indexCount = nx * ny * 6 * 2;

  const verts = new Float32Array(vertexCount * FLOATS_PER_VERTEX);
  const idx = new Uint32Array(indexCount);

  const hz = CARD.thickness / 2;

  let v = 0;
  for (let side = 0; side < 2; side++) {
    const front = side === 0;
    const z = front ? hz : -hz;
    const nz = front ? 1 : -1;

    for (let j = 0; j < rows; j++) {
      const ty = j / ny;
      for (let i = 0; i < cols; i++) {
        const tx = i / nx;

        verts[v++] = (tx - 0.5) * CARD.width;
        verts[v++] = (0.5 - ty) * CARD.height;
        verts[v++] = z;

        verts[v++] = 0;
        verts[v++] = 0;
        verts[v++] = nz;

        // The back face mirrors in X so its texture reads the right way round
        // when the card is flipped about its vertical axis.
        verts[v++] = front ? tx : 1 - tx;
        verts[v++] = ty;

        verts[v++] = nz;
      }
    }
  }

  let k = 0;
  for (let side = 0; side < 2; side++) {
    const base = side * perFace;
    const front = side === 0;
    for (let j = 0; j < ny; j++) {
      for (let i = 0; i < nx; i++) {
        const a = base + j * cols + i;
        const b = a + 1;
        const c = a + cols;
        const d = c + 1;
        // Both faces wound counter-clockwise when seen from outside.
        if (front) {
          idx[k++] = a; idx[k++] = c; idx[k++] = b;
          idx[k++] = b; idx[k++] = c; idx[k++] = d;
        } else {
          idx[k++] = a; idx[k++] = b; idx[k++] = c;
          idx[k++] = b; idx[k++] = d; idx[k++] = c;
        }
      }
    }
  }

  const vertices = device.createBuffer({
    label: 'card-mesh:vertices',
    size: verts.byteLength,
    usage: GPUBufferUsage.VERTEX | GPUBufferUsage.COPY_DST,
  });
  device.queue.writeBuffer(vertices, 0, verts);

  const indices = device.createBuffer({
    label: 'card-mesh:indices',
    size: idx.byteLength,
    usage: GPUBufferUsage.INDEX | GPUBufferUsage.COPY_DST,
  });
  device.queue.writeBuffer(indices, 0, idx);

  return {
    vertices, indices,
    indexCount, indexFormat: 'uint32',
    layout: CARD_VERTEX_LAYOUT,
  };
}

export function destroyCardMesh(mesh: CardMesh): void {
  mesh.vertices.destroy();
  mesh.indices.destroy();
}
